import { createContext, useContext, useState } from 'react'

// Spec §6 — only top-level navigable screens get a status. Sheets/overlays/sub-states
// (OTP sheet, bank-name-check) are local state on the screen that owns them (see
// `activeSheet`, `bankNameCheckStatus` below), not separate top-level statuses — this
// mirrors how Figma itself nests them.
//
// NOTE: the spec also defines a returning-user flow (R1-R4: recognised entry, confirm
// saved account, etc.). It's deliberately not built here - the backend has zero support
// for recognising a returning customer or looking up a saved account today (confirmed by
// reading the backend repo directly), so there was nothing to wire it to. See final report.
export class DirectDebitStatus {
  static ENTRY = 'entry'
  static YOUR_DETAILS = 'your-details'
  static ONE_TIME_TRANSFER = 'one-time-transfer'
  static MANDATE_CREATED = 'mandate-created'

  // Terminal / shared
  static FAILED = 'failed'
  static EXPIRED = 'expired'
  static ERROR = 'error'

  constructor(type) {
    this.type = type
  }
}

export const DirectDebitStatusContext = createContext(null)

export const DirectDebitStatusContextProvider = ({ children }) => {
  const [directDebitStatus, setDirectDebitStatus] = useState({
    status: DirectDebitStatus.ENTRY,
    message: '',
  })

  const [userEmail, setUserEmail] = useState('')
  const [userPhone, setUserPhone] = useState('')
  const [userAddress, setUserAddress] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [selectedBank, setSelectedBank] = useState({
    bankPublicId: '',
    bankName: '',
  })
  const [accountName, setAccountName] = useState('')
  // Surfaces a mandate-initiation failure back on YourDetailsScreen after the OTP sheet
  // (which is where initiateMandate now fires, post-verification) closes.
  const [detailsError, setDetailsError] = useState('')
  const [transferDetails, setTransferDetails] = useState(null)
  const [otpRequestedAt, setOtpRequestedAt] = useState(null)

  // Single field driving every bottom sheet (01b/03/05b) via AnimatePresence, instead of
  // one boolean per sheet.
  const [activeSheet, _setActiveSheet] = useState(null)

  // Replaces the old string-sniffed otpError heuristic in AccountDetailsScreen - spec §9
  // needs a real distinction between "account not found" and "bank unreachable".
  const [bankNameCheckStatus, setBankNameCheckStatus] = useState('idle')

  // Drives "Wrong code - N tries left" -> lockout (spec §7). Reset on resend. Client-side
  // only - the backend's OTP session model has retryCount/maxRetries internally but doesn't
  // expose them on the widget API today.
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(3)

  // Session-related state
  const [sessionReference, _setSessionReference] = useState(null)
  const [sessionKey, _setSessionKey] = useState(null)
  const [sessionId, _setSessionId] = useState(null)
  const [otpSessionId, _setOtpSessionId] = useState(null)
  const [otpChannel, _setOtpChannel] = useState('email')
  const [otpDestination, _setOtpDestination] = useState(null)
  const [sessionStatus, _setSessionStatus] = useState(null)
  const [checkoutUrl, _setCheckoutUrl] = useState(null)

  const showEntry = (message = '') => {
    setDirectDebitStatus({ status: DirectDebitStatus.ENTRY, message })
  }

  const showYourDetails = (message = '') => {
    setDirectDebitStatus({ status: DirectDebitStatus.YOUR_DETAILS, message })
  }

  const showOneTimeTransfer = (transferData, message = '') => {
    if (transferData) setTransferDetails(transferData)
    setDirectDebitStatus({ status: DirectDebitStatus.ONE_TIME_TRANSFER, message })
  }

  const showMandateCreated = (message = '') => {
    setDirectDebitStatus({ status: DirectDebitStatus.MANDATE_CREATED, message })
  }

  const showFailed = (message = '') => {
    setDirectDebitStatus({ status: DirectDebitStatus.FAILED, message })
  }

  const showExpired = (message = '') => {
    setDirectDebitStatus({ status: DirectDebitStatus.EXPIRED, message })
  }

  const showError = (message = '') => {
    setDirectDebitStatus({ status: DirectDebitStatus.ERROR, message })
  }

  const openSheet = (sheet) => {
    if (sheet === 'enter-code') setOtpRequestedAt(new Date())
    _setActiveSheet(sheet)
  }

  const closeSheet = () => {
    _setActiveSheet(null)
  }

  const resetFlow = () => {
    setDirectDebitStatus({ status: DirectDebitStatus.ENTRY, message: '' })
    setUserEmail('')
    setUserPhone('')
    setUserAddress('')
    setAccountNumber('')
    setSelectedBank({ bankPublicId: '', bankName: '' })
    setAccountName('')
    setDetailsError('')
    setTransferDetails(null)
    setOtpRequestedAt(null)
    _setActiveSheet(null)
    setBankNameCheckStatus('idle')
    setOtpAttemptsLeft(3)
  }

  const setErrorMessage = (message) => {
    setDirectDebitStatus((prevStatus) => ({
      ...prevStatus,
      message,
    }))
  }

  // Session-related setters
  const setSessionReference = (reference) => {
    _setSessionReference(reference)
  }

  const setSessionKey = (key) => {
    _setSessionKey(key)
  }

  const setSessionId = (id) => {
    _setSessionId(id)
  }

  const setOtpSessionId = (id) => {
    _setOtpSessionId(id)
  }

  const setOtpChannel = (channel) => {
    _setOtpChannel(channel)
  }

  const setOtpDestination = (destination) => {
    _setOtpDestination(destination)
  }

  const setSessionStatus = (status) => {
    _setSessionStatus(status)
  }

  const setCheckoutUrl = (url) => {
    _setCheckoutUrl(url)
  }

  const value = {
    directDebitStatus,
    userEmail,
    setUserEmail,
    userPhone,
    setUserPhone,
    userAddress,
    setUserAddress,
    accountNumber,
    setAccountNumber,
    selectedBank,
    setSelectedBank,
    accountName,
    setAccountName,
    detailsError,
    setDetailsError,
    transferDetails,
    setTransferDetails,
    otpRequestedAt,
    setOtpRequestedAt,
    activeSheet,
    openSheet,
    closeSheet,
    bankNameCheckStatus,
    setBankNameCheckStatus,
    otpAttemptsLeft,
    setOtpAttemptsLeft,
    sessionReference,
    setSessionReference,
    sessionKey,
    setSessionKey,
    sessionId,
    setSessionId,
    otpSessionId,
    setOtpSessionId,
    otpChannel,
    setOtpChannel,
    otpDestination,
    setOtpDestination,
    sessionStatus,
    setSessionStatus,
    checkoutUrl,
    setCheckoutUrl,
    showEntry,
    showYourDetails,
    showOneTimeTransfer,
    showMandateCreated,
    showFailed,
    showExpired,
    showError,
    resetFlow,
    setErrorMessage,
  }

  return (
    <DirectDebitStatusContext.Provider value={value}>
      {children}
    </DirectDebitStatusContext.Provider>
  )
}

export const useDirectDebitStatus = () => {
  return useContext(DirectDebitStatusContext)
}
